// @ts-types="yasqe/src/index.d.ts"
import Yasqe from "yasqe";
import "yasqe/build/yasqe.min.css";

// @ts-types="yasr/src/index.ts"
import Yasr from "yasr";
import "yasr/build/yasr.min.css";

import { css, html, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import "./components/Icons.ts";
import "./components/SettingsMenu.ts";
import { getEndpoint, isTrustedEndpoint } from "./endpoints.ts";

const queryPrefixes = {
  rdf: "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
  dwcFP: "http://filteredpush.org/ontologies/oa/dwcFP#",
  dwc: "http://rs.tdwg.org/dwc/terms/",
  dc: "http://purl.org/dc/elements/1.1/",
  trt: "http://plazi.org/vocab/treatment#",
  treatment: "https://treatment.plazi.org/id/",
  // taxonConcept: "https://taxon-concept.plazi.org/id/",
  // taxonName: "https://taxon-name.plazi.org/id/",
  publication: "https://publication.plazi.org/id/",
};

const resultsPrefixes = {
  bibo: "http://purl.org/ontology/bibo/",
  doi: "http://doi.org/",
  fabio: "http://purl.org/spar/fabio/",
};

const requestConfig = {
  showQueryButton: true,
  method: "GET",
  // headers: () => {
  //   return {};
  // },
};

const config = {
  autofocus: false,
  copyEndpointOnNewTab: true,
  createShareableLink: false,
  autoAddOnInit: true,
  prefixes: { ...queryPrefixes, ...resultsPrefixes },
  plugins: {
    table: {
      dynamicConfig: {
        compact: true,
      },
    },
  },
  // default query
  value: `
# This query gets ten arbitrary triples from the endpoint.
SELECT *
WHERE {
  ?sub ?pred ?obj .
}
LIMIT 10`,
  // render all lines
  editorHeight: "auto",
  viewportMargin: Infinity,
  inputStyle: "textarea",
  theme: "plazi",
};

@customElement("query-editor")
export class QueryEditor extends LitElement {
  static override styles = css`
  :host {
    display: block;
    margin: 1rem 0 2rem;
  }

  .yasqe .CodeMirror {
    height: auto;
    min-height: 50px;
    background: var(--nav-background);
    color: var(--text-color);
    border: none;
    border-radius: 1rem;
  }

  .yasqe .yasqe_buttons {
    top: 5px;
    right: 2.8166px;
  }

  .yasr {
    .yasr_btn {
      color: var(--text-color-muted);
      &:not(.disabled):hover {
        color: var(--text-color);
      }
    }
    .yasr_btnGroup .yasr_btn {
      padding-top: 0;
    }

    .yasr_btn.yasr_external_ref_btn {
      display: none;
    }

    .dataTables_wrapper table.dataTable tbody tr {
      background: var(--body-background);
      color: var(--text-color);

      &:hover {
        background: var(--highlight-background);
      }
    }
    
    .dataTables_wrapper .dataTables_filter, .dataTables_wrapper .dataTables_info, .dataTables_wrapper .dataTables_length, .dataTables_wrapper .dataTables_paginate, .dataTables_wrapper .dataTables_processing,
    .dataTables_wrapper .dataTables_paginate .paginate_button {
      color: var(--text-color-muted) !important;
    }
  }

  /* CodeMirror theme */
  .cm-s-plazi .cm-keyword { color: var(--text-color-muted); font-weight: 900; }
  .cm-s-plazi .cm-punc { color: var(--text-color); }
  .cm-s-plazi .cm-string-2 { color: var(--accent); }
  .cm-s-plazi .cm-string,
  .cm-s-plazi .cm-variable-3 { color: #4e69ae; }
  .cm-s-plazi .cm-atom { color: light-dark(rgb(139, 72, 9), rgb(185, 119, 58)); }
  .cm-s-plazi .cm-number { color: var(--accent); }
  .cm-s-plazi .cm-matchingbracket { color: var(--accent-mild); }
  .cm-s-plazi .cm-matchhighlight { background: var(--highlight-background); }
  .cm-s-plazi .cm-comment { color: var(--text-color-muted); font-style: italic; }
  .CodeMirror-gutters {
    background: var(--nav-background);
    border-color: var(--body-background);
  }
  .CodeMirror-linenumber {
    color: var(--text-color-muted);
  }

  /* colum-resizer */
  .grip-resizable{table-layout:fixed;} .grip-resizable > tbody > tr > td, .grip-resizable > tbody > tr > th{overflow:hidden}
  .grip-padding > tbody > tr > td, .grip-padding > tbody > tr > th{padding-left:0!important; padding-right:0!important;}
  .grip-container{ height:0px; position:relative;} .grip-handle{margin-left:-5px; position:absolute; z-index:5; }
  .grip-handle .grip-resizable{position:absolute;background-color:red;filter:alpha(opacity=1);opacity:0;width:10px;height:100%;cursor: col-resize;top:0px}
  .grip-lastgrip{position:absolute; width:1px; } .grip-drag{ border-left:1px dotted black;	}
  .grip-flex{width:auto!important;} .grip-handle.grip-disabledgrip .grip-resizable{cursor:default; display:none;}
  `;

  @property()
  accessor persistenceId: string | false = false;

  @property()
  accessor endpoint: string = "https://treatment.ld.plazi.org/sparql";

  @property()
  accessor query: string | null = null;

  private yasqe?: Yasqe;
  private editor?: HTMLDivElement;

  override render() {
    // the editor is created once, so that results and edits survive updates
    this.editor ??= this.createEditor();
    return html`<link href="advanced.css" rel="stylesheet">${this.editor}`;
  }

  protected override updated(changed: PropertyValues<this>) {
    if (changed.has("endpoint") && this.yasqe) {
      // used for the next query run
      (this.yasqe.config.requestConfig as { endpoint: string }).endpoint =
        this.endpoint;
    }
  }

  private createEditor() {
    // deno-lint-ignore no-explicit-any
    const newConfig: Record<string, any> = {
      ...config,
      requestConfig: {
        ...requestConfig,
        endpoint: this.endpoint,
      },
      persistenceId: this.persistenceId,
    };
    const div = document.createElement("div");
    const yasqe = new Yasqe(div, newConfig);
    this.yasqe = yasqe;
    yasqe.addPrefixes(queryPrefixes);
    yasqe.collapsePrefixes(true);
    const yasr = new Yasr(div, newConfig);
    yasqe.on(
      "queryResponse",
      (
        _instance: Yasqe,
        req: unknown,
        duration: number,
      ) => {
        yasr.setResponse(req, duration);
      },
    );

    if (this.query !== null) {
      yasqe.setValue(this.query);
      yasqe.addPrefixes(queryPrefixes);
      yasqe.collapsePrefixes(true);
    }
    globalThis.requestAnimationFrame(() => {
      yasqe.refresh();
    });

    return div;
  }
}

type exampleQuery = {
  title: string;
  description: string;
  query: string;
};
const exampleQueries: exampleQuery[] = [
  {
    title: "Describing objects",
    description: `
    A good way to get get a first impression as to how the data is structured is using <code class="uri">DESCRIBE</code>-queries.
    Try also to describe the objects that this first query returns.
    `,
    query: `DESCRIBE <https://treatment.plazi.org/id/962587B2FFF8FF9EFF2A38EEF59DB87E>`,
  },
  {
    title: "All synonyms",
    description: `
    This query returns all synonyms of 
    <code class="uri taxon">https://taxon-concept.plazi.org/id/Animalia/Tyrannosaurus_rex_Osborn_1905</code>.
    It uses a transitive property path to get the taxa (Taxon-Concepts) 
    augmented or defined by a treatmented that deprecates this taxon or that are
    deprecated by a treatement that defines or deprecates this taxon.
    `,
    query: `SELECT DISTINCT *
WHERE {
  <https://taxon-concept.plazi.org/id/Animalia/Tyrannosaurus_rex_Osborn_1905> ((^trt:deprecates/(trt:augmentsTaxonConcept|trt:definesTaxonConcept))|((^trt:augmentsTaxonConcept|^trt:definesTaxonConcept)/trt:deprecates))* ?tc .
}`,
  },
  {
    title: "Find homonyms",
    description: `Find binomens that exist both as Animalia as well as Plantae`,
    query: `SELECT DISTINCT *
WHERE {
  ?s1 a dwcFP:TaxonName ;
      dwc:kingdom "Plantae" ;
      dwc:genus ?gn ;
      dwc:species ?sp ;
      dwc:rank "species" .
  ?s2 a dwcFP:TaxonName ;
      dwc:kingdom "Animalia" ;
      dwc:genus ?gn ;
      dwc:species ?sp ;
      dwc:rank "species" .
}
LIMIT 100`,
  },
  {
    title: "Find kingdoms",
    description:
      `Find all kingdoms and one sample taxon-name from within each.`,
    query: `SELECT DISTINCT ?k (SAMPLE(?s1) as ?s)
WHERE {
  ?s1 a dwcFP:TaxonName ;
     dwc:kingdom ?k .
}
GROUP BY ?k`,
  },
  {
    title: "Largest graphs",
    description:
      `This query returns the 10 largest graphs on the SPARQL endpoint.`,
    query: `SELECT DISTINCT ?g (COUNT(?sub) AS ?size)
WHERE {
  GRAPH ?g { ?sub ?pred ?obj . }
}
GROUP BY ?g
ORDER BY DESC(?size)
LIMIT 10`,
  },
  {
    title: "Single graph",
    description: `
    This query returns the triples of a single graph.
    Note that this works only on endpoints where each treatment receives its own graph, such as the Plazi and QLever endpoints, but not LINDAS.`,
    query: `CONSTRUCT { ?s ?p ?o . }
WHERE {
  GRAPH <https://treatment.plazi.org/id/8E33E30FFFD9FFCC4AD302B2FFB04209> {
    ?s ?p ?o .
  }
}`,
  },
  {
    title: "Unusual Names",
    description:
      `This query returns genus with contain a character from outside the basic latin A-Z.`,
    query: `SELECT DISTINCT ?graph ?genus
WHERE { 
  GRAPH ?graph {
    ?sub dwc:genus ?genus .
    ?sub dwc:species ?species .
    ?sub a dwcFP:TaxonName .
    FILTER regex(?genus, "[^a-zA-Z]")
  }
}
ORDER BY UCASE(?genus)
LIMIT 100`,
  },
  {
    title: "Treatments by genus",
    description: "", // TODO
    query: `SELECT DISTINCT *
WHERE { 
  ?tc dwc:rank ?rank ;
      dwc:phylum ?phylum ;
      dwc:kingdom ?kingdom ;
      dwc:class ?class ;
      dwc:family ?family ;
      dwc:order ?oder ;
      dwc:genus "Lestes" ;
      a dwcFP:TaxonConcept .
  OPTIONAL {
    ?tc trt:hasTaxonName ?tn .
  }
  OPTIONAL {
    ?augmentingTreatment trt:augmentsTaxonConcept ?tc .
    ?augmentingTreatment dc:creator ?augmentingTreatmentCreator .
  }
  OPTIONAL {
    ?definingTreatment trt:definesTaxonConcept ?tc .
    ?definingTreatment dc:creator ?definingTreatmentCreator .
  }
}`,
  },
  {
    title: "List material citations",
    description: "", // TODO
    query: `SELECT ?s WHERE { ?s a dwc:MaterialCitation . } LIMIT 10`,
  },
  {
    title: "List specific material citations",
    description:
      `This query returns all material citations for all synonyms of Tyrannosaurus rex that are in the New Mexico Museum of Natural History & Science (NMMNH).`,
    query: `SELECT DISTINCT ?syn ?catalogNumber # ?collectionCode
WHERE {
  <https://taxon-concept.plazi.org/id/Animalia/Tyrannosaurus_rex_Osborn_1905> ((^trt:deprecates/(trt:augmentsTaxonConcept|trt:definesTaxonConcept))|((^trt:augmentsTaxonConcept|^trt:definesTaxonConcept)/trt:deprecates))* ?syn .
  ?treat (trt:definesTaxonConcept|trt:augmentsTaxonConcept|trt:deprecates) ?syn ;
         dwc:basisOfRecord ?mc .
  # This might miss some specimens where the ‘dwc:collectionCode’ was entered in a non-standard way.
  # Replace ‘"NMMNH"’ with ‘?collectionCode’ to remove this restriction.
  # You may then add ‘?collectionCode’ to the ‘SELECT’ line to see each specimen’s collection.
  ?mc dwc:collectionCode "NMMNH" ;
      dwc:catalogNumber ?catalogNumber .
}`,
  },
];

@customElement("syno-advanced")
export class SynoAdvanced extends LitElement {
  static override styles = css`
  h4 {
    margin-bottom: 0.2rem;
  }
  h4 + p {
    margin-top: 0;
  }
  `;

  @state()
  accessor endpoint: string = getEndpoint();

  /** Whether the endpoint is an unfamiliar one, set by the link. */
  @state()
  accessor endpointFromLink: boolean = !isTrustedEndpoint(this.endpoint);

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("endpoint-change", this.onEndpointChange);
  }

  override disconnectedCallback() {
    document.removeEventListener("endpoint-change", this.onEndpointChange);
    super.disconnectedCallback();
  }

  /** An endpoint chosen in the settings menu applies to all editors. */
  private readonly onEndpointChange = (e: Event) => {
    this.endpoint = (e as CustomEvent<string>).detail;
    this.endpointFromLink = false;
    // the server named in the link no longer applies
    const url = new URL(document.location.href);
    if (url.searchParams.has("server")) {
      url.searchParams.delete("server");
      history.replaceState(null, "", url);
    }
  };

  override render() {
    return html`
      <link href="index.css" rel="stylesheet">
      <h2>SPARQL Queries</h2>
      <p>
        Here, you can run arbitrary
        <a target="_blank" href="https://www.w3.org/TR/sparql11-query/" class="uri">SPARQL<s-icon icon="link"></s-icon></a>
        queries against our data. The queries are sent to the endpoint chosen
        in the settings (the gear icon above).
      </p>
      ${
      this.endpointFromLink
        ? html`<p><b>Note:</b> This link set the server to
        <code class="uri">${this.endpoint}</code>, which is not one of the
        known endpoints nor the one chosen in the settings. Queries are sent
        to that server; choose an endpoint in the settings menu to switch.</p>`
        : nothing
    }
      <query-editor persistenceId="editor-1" endpoint=${this.endpoint}></query-editor>
      <query-editor persistenceId="editor-2" endpoint=${this.endpoint}></query-editor>
      <hr>
      <h3>Example Queries</h3>
      <p><small>(You can reset the following editors by reloading the page)</small></p>
      ${
      exampleQueries.map((example) =>
        html`<section>
          <h4>${example.title}</h4>
          <p>${unsafeHTML(example.description)}</p>
          <query-editor query=${example.query} endpoint=${this.endpoint}></query-editor>
        </section>`
      )
    }
    `;
  }
  // protected override createRenderRoot() {
  //   return this;
  // }
}
